import {useLocation, useNavigate, useParams} from "react-router-dom";
import {
    Badge,
    Button,
    Checkbox,
    Col,
    Input,
    message,
    Row,
    Select,
    Space,
    Spin,
    Table,
    TableProps,
    Tooltip,
    Typography
} from "antd";
import React, {useEffect, useState,useRef} from "react";
import {UtilsService} from "../../../services/Utils.service";
import {
    ArrowRightOutlined,
    CheckOutlined,
    DownloadOutlined,
    EyeOutlined,
    MinusOutlined,
    SearchOutlined
} from "@ant-design/icons";
import {HttpService} from "../../../services/http/Http.service";
import DetailModal from "../workOnPractices/DetailModal";
import {PracticeExportService} from "../PracticeExport.service";
import Title from "antd/es/typography/Title";
import {SelectAllButton} from "../SelectAllButton";
import {
    RoutingURL,
    SendStatusLabel,
    SendStatusType,
    SimulatedFileTS,
    SimulatorPersonIdPair,
    StatusCodeE
} from "../../../model/models";
import {TemplateUtils} from "../../../services/utils/TemplateUtils";
import {combinatorColumns} from "../combinatorColumns";
import PracticeNotify from "../../../components/practices/PracticeNotify";
import {usePaginatedData} from "../../../services/hooks/usePaginatedData";
import {HttpErrorService} from "../../../services/http/HttpError.service";
import {SendUtils} from "../../../services/utils/SendUtils";
import UserDetailsButton from "../../userDetails/UserDetailsButton";
import PracticeSendNotify from "../../../components/practices/PracticeSendNotify";
import {AuthService} from "../../../services/Auth.service";

export default function CommunicatedPractices() {
    const navigate = useNavigate();
    const {id} = useParams();
    const location = useLocation();
    const [ignoreSendIntermediateStatuses, setIgnoreSendIntermediateStatuses] = useState(true);
    const [filterHasProtocolOnly, setFilterHasProtocolOnly] = useState(false);
    const [sendStatusFilter, setSendStatusFilter] = useState<string | undefined>(undefined);



    const {
        data: filteredData,
        totalItems,
        pageData,
        searchText,
        setSearchText,
        handleTableChange,
        reloadCurrentPage,
        loadData
    } = usePaginatedData<SimulatedFileTS>({
        simulatorId: id,
        status: StatusCodeE.NOTIFICA,
        initialPageSize: 20,
        ignoreSendIntermediateStatuses: ignoreSendIntermediateStatuses,
        filterHasProtocolOnly: filterHasProtocolOnly,
        sendStatusFilter: sendStatusFilter
    });

    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
    const [combinatorData, setCombinatorData] = useState(location.state?.simulatorData);
    const [selectedTotal, setSelectedTotal] = useState(0);
    const [isOpen, setIsOpen] = useState(false)
    const [userId, setUserId] = useState<number>()
    const [username, setUsername] = useState<string>("")
    const [fiscalCode, setFiscalCode] = useState<string>("")
    const [type, setType] = useState<string>("")
    const [isRateApplied, setIsRateApplied] = useState<boolean>(false)
    const [loadingPersonId, setLoadingPersonId] = useState<number | null>(null);
    const sendStatusOptions = Object.entries(SendStatusLabel).map(([value, label]) => ({label, value}));
    const skipInitialNotifyFiltersLoad = useRef(true);

    useEffect(() => {
        if (!combinatorData) {
            HttpService.getInstanceAxios().get(`/simulazione/${id}/statistiche`)
                .then(response => {
                    const item = response.data || {};

                    const { simulator, statistics } = item;
                    const { filters } = simulator;

                    let yearLabel = filters.years.join(" - ");
                    if (filters.checked) {
                        yearLabel += " (Anni indicati)";
                    }

                    let exemptionLabel = "Tutti";
                    if (filters.exemptionCodes?.length > 0) {
                        exemptionLabel = filters.exemptionCodes.join(" - ");
                    }
                    if (filters.exemptionCodeChecked) {
                        exemptionLabel += " (Codici indicati)";
                    }
                    setCombinatorData([{
                        key: simulator.id,
                        numberPositions: statistics.totalOccurrences,
                        numberAssisted: statistics.totalPerson,
                        exemptionCode: exemptionLabel,
                        year: yearLabel,
                        type: statistics.typeList?.join(" - "),
                        amount: "€ " + UtilsService.formatAmountRange([filters.minAmount, filters.maxAmount]),
                        ticket: "€ " + UtilsService.formatNumber(statistics.totalTicket),
                    }]);
                })
        }
    }, [combinatorData]);

    useEffect(() => {
        if (userId !== null && userId !== undefined) {
            setIsOpen(true);
        }
    }, [userId]);

    useEffect(() => {
        if (skipInitialNotifyFiltersLoad.current) {
            skipInitialNotifyFiltersLoad.current = false;
            return;
        }

        loadData(0, pageData.pageSize);
    }, [ignoreSendIntermediateStatuses, filterHasProtocolOnly, sendStatusFilter]);


    const rowSelection: TableProps<SimulatedFileTS>['rowSelection'] = {
        selectedRowKeys: selectedRowKeys,
        onChange: (selectedRowKeys: React.Key[], selectedRows: SimulatedFileTS[]) => {
            const total = selectedRows.reduce((sum, row) => sum + row.totalSum, 0);
            setSelectedTotal(total);
            setSelectedRowKeys(selectedRowKeys)
        },

    };

    const getMaxProtocolDateFromSelected = (): Date | null => {
        if (selectedRowKeys.length === 0 || !filteredData) return null;
        const selectedPersonIds = selectedRowKeys.map(key => Number(key));
        const selectedRows = filteredData.filter(row => selectedPersonIds.includes(row.personId));

        const protocolDates = selectedRows
            .map(row => row.protocolDate)
            .filter(date => date)
            .map(date => new Date(date));

        if (protocolDates.length === 0){
            return null;
        }

        return new Date(Math.max(...protocolDates.map(date => date.getTime())));
    };

    async function exportToExcel(xlsx: string) {
        if (!id) return;
        setIsExporting(true);
        try {
            await PracticeExportService.loadAndExportToExcel(Number.parseInt(id), StatusCodeE.NOTIFICA, xlsx);
        } finally {
            setIsExporting(false);
        }
    }

    const handleModal = (record: SimulatedFileTS) => {
        setUserId(record.personId)
        setUsername(record.fullName)
        setFiscalCode(record.fiscalCode)
        setType(record.type)
        setIsRateApplied(record.hasInstallment)
    }

    const handleConfirmDate = async () => {
        if (AuthService.isAdmin()) {
            await reloadCurrentPage()
        } else {
            let url: string = UtilsService.replaceId(RoutingURL.PRACTICES, id);
            url = UtilsService.replaceStatus(url, StatusCodeE.ATTESA_PAGAMENTO);
            navigate(url);
        }
    };

    const handleSendNotification = async () => {
        reloadCurrentPage();
    };

    const [isExporting, setIsExporting] = useState(false);
    const [onBackLoading, setOnBackLoading] = useState(false);

    const onBack = async () => {
        setOnBackLoading(true);
        if (selectedRowKeys.length > 0) {
            try {
                await HttpService.getInstanceAxios().post(`/file-ts/${id}/previous-state`, selectedRowKeys)
                let url: string = UtilsService.replaceId(RoutingURL.PRACTICES, id);
                url = UtilsService.replaceStatus(url, StatusCodeE.DA_FIRMARE);
                navigate(url);
            } catch (error) {
                HttpErrorService.handle("Errore durante la rielaborazione delle pratiche", error, true)
            } finally {
                setOnBackLoading(false);
            }
        } else message.error("Nessun utente selezionato.")
    }

    const columns = [
        {
            title: "Codice Fiscale",
            dataIndex: "fiscalCode",
            key: "fiscalCode",
            sorter: (a: any, b: any) => a.fiscalCode.localeCompare(b.fiscalCode),
        },
        {
            title: "Cognome Nome",
            dataIndex: "fullName",
            key: "fullName",
            sorter: (a: any, b: any) => a.fullName.localeCompare(b.fullName),
        },
        {
            title: "Anno",
            dataIndex: "years",
            key: "years",
            render: (years: string[]) => years.sort((a, b) => parseInt(a) - parseInt(b)).join(" - "),
        },
        {
            title: "Codici esenzione",
            dataIndex: "exemptionCodes",
            key: "exemptionCodes",
            sorter: (a: any, b: any) => a.exemptionCodes.join(", ").localeCompare(b.exemptionCodes.join(", ")),
            render: (codes: string[]) => codes.join(" - "),
        },
        {
            title: "Tipo",
            dataIndex: "type",
            key: "type"
        },
        {
            title: "Rate",
            dataIndex: "hasInstallment",
            key: "hasInstallment",
            render: (value: boolean) => (value ? <CheckOutlined style={{color: 'var(--main-color)'}}/> :
                <MinusOutlined/>),
        },
        {
            title: "PagoPA",
            dataIndex: "hasPayment",
            key: "hasPayment",
            render: (value: boolean) => (value ? <CheckOutlined style={{color: 'var(--main-color)'}}/> :
                <MinusOutlined/>),
        },
        {
            title: "Interessi",
            dataIndex: "totalInterest",
            key: "totalInterest",
            render: (value: number) => (value > 0 ? <CheckOutlined style={{color: 'var(--main-color)'}}/> :
                <MinusOutlined/>),
        },
        {
            title: "Importo totale",
            dataIndex: "totalSum",
            key: "totalSum",
            sorter: (a: any, b: any) => (a.totalSum ?? 0) - (b.totalSum ?? 0),
            render: (value: number) => "€ " + UtilsService.formatNumber(value),
        },
        {
            title: "No. Protocollo",
            dataIndex: "protocolNumbers",
            key: "protocolNumbers",
            render:  ((protocolNumbers: string[]) => protocolNumbers.join(" - "))
        },
        {
            title: "Stato SEND",
            dataIndex: "sendStatus",
            key: "sendStatus",
            render: (value: string) =>
                (value ?  <Typography.Text type={SendUtils.getStatusColor(value as SendStatusType)}>
                        {SendStatusLabel[value as SendStatusType]}</Typography.Text> :
                    <MinusOutlined/>)
        },
        {
            title: "Azioni",
            key: "operation",
            render: (record: SimulatedFileTS) => (
                <Space>
                    <Tooltip placement="top" title={'Visualizza Glifo generato'}>
                        <Button
                            onClick={() => {
                                setLoadingPersonId(record.personId);
                                TemplateUtils.downloadGlyphedDocument(
                                    combinatorData[0].key,
                                    record.personId,
                                    () => setLoadingPersonId(null) // callback a fine operazione
                                );
                            }}
                            icon={
                                loadingPersonId === record.personId ? <Spin size="small"/> : <EyeOutlined/>
                            }
                            disabled={loadingPersonId === record.personId}
                        />
                    </Tooltip>
                    <Button onClick={() => handleModal(record)} type={"primary"} icon={<ArrowRightOutlined/>}/>
                    <UserDetailsButton personID={record.personId} combinatorID={Number(id)}></UserDetailsButton>
                </Space>
            ),
        },
    ];

    return (
        <>
            <Row justify="space-between" align="middle">
                <Col>
                    <Input
                        style={{width: "400px"}}
                        size="middle"
                        allowClear
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        addonBefore={<SearchOutlined/>}
                        placeholder="Nome, Cognome, Codice Fiscale"
                    />
                </Col>
                <Col>
                    <Space>
                        {!ignoreSendIntermediateStatuses && (
                            <Select
                                allowClear
                                placeholder="Stato Send"
                                style={{minWidth: 200}}
                                options={sendStatusOptions}
                                value={sendStatusFilter}
                                onChange={(value) => setSendStatusFilter(value)}
                            />
                        )}
                        <Checkbox
                            checked={filterHasProtocolOnly}
                            onChange={(e) => setFilterHasProtocolOnly(e.target.checked)}
                            className={`solo-pagopa ${filterHasProtocolOnly ? 'checked' : ''}`}
                        >
                            Solo protocollate
                        </Checkbox>
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            disabled={selectedRowKeys.length === 0}
                            onClick={() =>
                                TemplateUtils.downloadSignedDocumentsAsZip(combinatorData[0].key, selectedRowKeys as number[], () => {
                                        setSelectedRowKeys([]);
                                        setSelectedTotal(0);
                                    }
                                )
                            }
                        >
                            Download
                        </Button>
                        <Button
                            className="custom-label-color"
                            type="primary"
                            icon={<DownloadOutlined/>}
                            loading={isExporting}
                            onClick={() => exportToExcel("Report.xlsx")}
                        >
                            Esporta
                        </Button>
                    </Space>
                </Col>
            </Row>
            <Space size={30} direction={"vertical"} style={{width: "100%"}}>
                <Table
                    columns={combinatorColumns}
                    dataSource={combinatorData}
                    pagination={false}
                    bordered
                />
                <Space style={{display: "flex", justifyContent: "space-between"}}>
                    {id && <SelectAllButton
                        idSimulator={id}
                        status={StatusCodeE.NOTIFICA}
                        onSelected={(keys) => {
                            setSelectedRowKeys(keys);
                            const keyNumbers = keys.map(k => Number(k));
                            const total = filteredData
                                .filter(row => keyNumbers.includes(row.personId))
                                .reduce((sum, row) => sum + (row.totalSum || 0), 0);

                            setSelectedTotal(total);
                        }}
                    />

                    }
                    <div>
                        Utenti selezionati: <Badge color={"#007A33FF"}
                                                   count={selectedRowKeys ? selectedRowKeys.length : 0} showZero={true}
                                                   overflowCount={9999}
                                                   style={{fontSize: "14px", transform: "scale(1.1)"}}/>
                    </div>

                    <Space>
                        <Button
                            type="primary"
                            disabled={!ignoreSendIntermediateStatuses}
                            onClick={() => {
                                setIgnoreSendIntermediateStatuses(false);
                                setSendStatusFilter(undefined);
                            }}
                        >
                            Mostra tutti
                        </Button>

                        <Button
                            type="primary"
                            disabled={ignoreSendIntermediateStatuses}
                            onClick={() => {
                                setIgnoreSendIntermediateStatuses(true);
                                setSendStatusFilter(undefined);
                            }}
                        >
                            Mostra da notificare
                        </Button>
                    </Space>
                </Space>
                <Table<SimulatedFileTS>
                    rowSelection={{type: "checkbox", ...rowSelection}}
                    rowKey={record => record.personId}
                    dataSource={filteredData}
                    columns={columns}
                    pagination={{
                        total: totalItems,
                        showTotal: (total) => `Totale ${total} elementi`,
                        current: pageData.current,
                        pageSize: pageData.pageSize,
                        pageSizeOptions: ["10", "20", "50", "100", "250", "500"],
                        showSizeChanger: true,
                    }}
                    onChange={handleTableChange}
                />
            </Space>
            <Space className={"custom-space"}>
                <Button onClick={onBack} size={"large"} type={"dashed"} loading={onBackLoading} danger>Rielabora pratiche</Button>
                {id && <PracticeSendNotify
                    title={'Notifica con SEND'}
                    selectedSimulatorPersonPairs={selectedRowKeys.map(r => {
                        return {
                            simulatorId: Number.parseInt(id),
                            personId: r
                        } as SimulatorPersonIdPair})}
                    onSuccess={handleSendNotification}
                />}
                {id && <PracticeNotify
                    title={'Notifica manualmente'}
                    selectedSimulatorPersonPairs={selectedRowKeys.map(r => {
                        return {
                            simulatorId: Number.parseInt(id),
                            personId: r
                        } as SimulatorPersonIdPair})}
                    onSuccess={handleConfirmDate}
                    minProtocolDate={getMaxProtocolDateFromSelected()}
                />}
            </Space>
            <Title className={"custom-title"} level={5}>Importo selezionato:
                € {UtilsService.formatNumber(selectedTotal)}</Title>
            {isOpen ?
                <DetailModal isOpen={isOpen}
                             combinatorId={id}
                             setIsOpen={setIsOpen}
                             userId={userId}
                             setUserId={setUserId}
                             username={username}
                             fiscalCode={fiscalCode}
                             type={type}
                             isRateApplied={isRateApplied}
                />
                : null
            }
        </>
    )
}
